import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';

import {
  Button,
  HStack,
  ScrollPane,
  TableView,
  useShowMessage,
  VStack,
} from '../../../../core-ui';
import { confirm } from '../../../App/AppDialog';
import { FlatFeesTableFooter } from './FlatFeesTableFooter';
import { CREATE_FLAT_FEE, DELETE_FLAT_FEE, GET_FLAT_FEES } from '../graphql';

import { GetFlatFees } from '../__generated__/GetFlatFees';
import {
  CreateFlatFee,
  CreateFlatFeeVariables,
} from '../__generated__/CreateFlatFee';
import {
  DeleteFlatFee,
  DeleteFlatFeeVariables,
} from '../__generated__/DeleteFlatFee';
import { useShowApolloError } from '../../../../support/graphql/useShowApolloError';
import formatCurrency from '../../../../core/support/formatCurrency';
import { useSeason } from '../../../SeasonState';

export function FlatFeesTable() {
  const [feeAmount, setFeeAmount] = useState(0);
  const [feeName, setFeeName] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const showMessage = useShowMessage();
  const showApolloError = useShowApolloError();
  const { season } = useSeason();
  const seasonId = season?.id ?? '';

  const { data } = useQuery<GetFlatFees>(GET_FLAT_FEES, {
    fetchPolicy: 'cache-and-network',
    variables: {
      seasonId,
    },
  });

  const [createFlatFee, { loading: isSaving }] = useMutation<
    CreateFlatFee,
    CreateFlatFeeVariables
  >(CREATE_FLAT_FEE, {
    onError: showApolloError,
    onCompleted: () => {
      showMessage('success', t('Flat fee created successfully'));
    },
    refetchQueries: [GET_FLAT_FEES],
  });

  const [deleteFlatFee, { loading: isDeleting }] = useMutation<
    DeleteFlatFee,
    DeleteFlatFeeVariables
  >(DELETE_FLAT_FEE, {
    onError: showApolloError,
    onCompleted: () => {
      showMessage('success', t('Flat fee deleted successfully'));
    },
    refetchQueries: [GET_FLAT_FEES],
  });

  const flatFees = data?.flatFees ?? [];
  const currency = data?.myAccount.settings.currency ?? '';

  return (
    <VStack>
      <ScrollPane storeKey="flat-fees-table" maxHeight="60vh" width="100%">
        <TableView
          data={flatFees}
          columns={[
            {
              title: t('Name'),
              render: (fee) => fee.name,
              tdProps: { maxWidth: 300 },
              sortable: { sortFn: (a, b) => a.name.localeCompare(b.name) },
            },
            {
              title: t('Amount'),
              render: (fee) => formatCurrency(fee.amountInCents, currency),
            },
            {
              title: '',
              render: (redemption) => (
                <HStack justify="flex-end">
                  <Button
                    onClick={async () => {
                      const proceed = await confirm(
                        t('Delete fee?'),
                        t('This will permanently delete this fee.'),
                      );
                      if (proceed) {
                        await deleteFlatFee({
                          variables: { id: redemption.id },
                        });
                      }
                    }}
                    isLoading={isDeleting}
                  >
                    {t('Delete')}
                  </Button>
                </HStack>
              ),
              tdProps: { width: '1%', cursor: 'default' },
              onClick: (event) => event.stopPropagation(),
            },
          ]}
        />
      </ScrollPane>
      <FlatFeesTableFooter
        currency={currency}
        feeAmount={feeAmount}
        feeName={feeName}
        isSaving={isSaving}
        isModalOpen={isModalOpen}
        onClick={() => setIsModalOpen(true)}
        onClose={() => {
          setFeeName('');
          setFeeAmount(0);
          setIsModalOpen(false);
        }}
        onFeeAmountChange={(value) => setFeeAmount(value)}
        onFeeNameChange={(event) => setFeeName(event.target.value)}
        onSubmit={async () => {
          await createFlatFee({
            variables: {
              flatFeeInput: { amountInCents: feeAmount, name: feeName },
              seasonId,
            },
          });
          setFeeName('');
          setFeeAmount(0);
          setIsModalOpen(false);
        }}
      />
    </VStack>
  );
}
